import { useTranslation } from 'react-i18next'

interface PaginationProps {
  page: number
  totalPages: number
  onChange: (page: number) => void
}

function buildPages(page: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages: (number | '...')[] = [1]
  if (page > 3) pages.push('...')
  for (let p = Math.max(2, page - 1); p <= Math.min(total - 1, page + 1); p++) pages.push(p)
  if (page < total - 2) pages.push('...')
  pages.push(total)
  return pages
}

export function Pagination({ page, totalPages, onChange }: PaginationProps) {
  const { t } = useTranslation()

  if (totalPages <= 1) return null

  const pages = buildPages(page, totalPages)

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, marginTop: 24 }}>
      <PageBtn onClick={() => onChange(page - 1)} disabled={page === 1} aria-label={t('pagination.previousPage')}>
        {'<'}
      </PageBtn>

      {pages.map((p, i) =>
        p === '...' ? (
          <span key={`ellipsis-${i}`} style={{ padding: '0 4px', fontSize: 13, color: 'var(--text-dim)' }}>
            …
          </span>
        ) : (
          <PageBtn
            key={p}
            onClick={() => onChange(p)}
            disabled={p === page}
            active={p === page}
          >
            {p}
          </PageBtn>
        ),
      )}

      <PageBtn onClick={() => onChange(page + 1)} disabled={page === totalPages} aria-label={t('pagination.nextPage')}>
        {'>'}
      </PageBtn>
    </div>
  )
}

function PageBtn({
  children,
  onClick,
  disabled,
  active,
  'aria-label': ariaLabel,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
  active?: boolean
  'aria-label'?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={active ? 'page' : undefined}
      style={{
        minWidth: 32,
        height: 32,
        padding: '0 8px',
        borderRadius: 6,
        border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
        background: active ? 'rgba(59,130,246,.12)' : 'transparent',
        color: active ? 'var(--accent)' : disabled && !active ? 'var(--text-dim)' : 'var(--text)',
        fontSize: 13,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled && !active ? 0.4 : 1,
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      {children}
    </button>
  )
}
